import React, { useState, useEffect } from 'react';
import { Platform_earnings, List_country, List_city } from '../../Utils/Api';
import { toast } from 'react-toastify';

const PlatformEarnings = () => {
    const [loading, setLoading] = useState(true);
    const [earnings, setEarnings] = useState([]);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Filter states
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [filters, setFilters] = useState({
        location_id: '',
        start_date: '',
        end_date: ''
    });

    useEffect(() => {
        fetchCountries();
    }, []);

    useEffect(() => {
        fetchEarnings();
    }, [page, filters]);

    const fetchCountries = async () => {
        try {
            const response = await List_country();
            if (response && response.data?.data) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const fetchCities = async (countryId) => {
        try {
            const response = await List_city(countryId);
            if (response && response.data?.data) {
                setCities(response.data.data);
            } else {
                setCities([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        }
    };

    const fetchEarnings = async () => {
        setLoading(true);
        try {
            const params = { 
                page, 
                limit: 10,
                ...filters
            };
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '') delete params[key];
            });

            const response = await Platform_earnings(params);
            if (response.data && response.data.data) {
                const { gig_fee_breakdown, platform_fee_summary, pagination } = response.data.data;
                
                // Convert cents to dollars
                const processedBreakdown = (gig_fee_breakdown || []).map(item => ({
                    ...item,
                    transaction_value: (item.transaction_value || 0) / 100,
                    platform_fee_value: (item.platform_fee_value || 0) / 100
                }));

                const processedSummary = platform_fee_summary ? {
                    ...platform_fee_summary,
                    total_platform_fees: (platform_fee_summary.total_platform_fees || 0) / 100,
                    total_transaction_value: (platform_fee_summary.total_transaction_value || 0) / 100,
                    total_finalized_amount: (platform_fee_summary.total_finalized_amount || 0) / 100
                } : null;

                setEarnings(processedBreakdown);
                setSummary(processedSummary);
                if (pagination) {
                    setTotalPages(pagination.total_pages || 1);
                }
            }
        } catch (error) {
            console.error("Error fetching platform earnings:", error);
            toast.error("Failed to load earnings data");
        } finally {
            setLoading(false);
        }
    };

    const handleCountryChange = (e) => {
        const countryId = e.target.value;
        setSelectedCountry(countryId);
        setFilters(prev => ({ ...prev, location_id: '' }));
        if (countryId) {
            fetchCities(countryId);
        } else {
            setCities([]);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1); // Reset to first page on filter change
    };

    const clearFilters = () => {
        setFilters({
            location_id: '',
            start_date: '',
            end_date: ''
        });
        setSelectedCountry('');
        setCities([]);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    return (
        <div className="main-content">
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                <h1 className="page-title fw-semibold fs-18 mb-0">Platform Earnings</h1>
                <div className="ms-md-1 ms-0">
                    <nav>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item"><a href="#">Management</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Platform Earnings</li>
                        </ol>
                    </nav>
                </div>
            </div>

            {/* Filters Section */}
            <div className="card custom-card">
                <div className="card-header d-flex justify-content-between">
                    <div className="card-title">Filters</div>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Country</label>
                            <select
                                className="form-select"
                                value={selectedCountry}
                                onChange={handleCountryChange}
                            >
                                <option value="">Select Country</option>
                                {countries.map(country => (
                                    <option key={country._id} value={country._id}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">City (Location)</label>
                            <select
                                className="form-select"
                                name="location_id"
                                value={filters.location_id}
                                onChange={handleFilterChange}
                                disabled={!selectedCountry}
                            >
                                <option value="">All Cities</option>
                                {cities.map(city => (
                                    <option key={city._id} value={city._id}>
                                        {city.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="start_date"
                                value={filters.start_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="end_date"
                                value={filters.end_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                className="btn btn-outline-danger w-100"
                                onClick={clearFilters}
                            >
                                <i className="bx bx-reset me-1"></i> Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row">
                <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                    <div className="card custom-card overflow-hidden">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <span className="avatar avatar-md p-2 bg-primary-transparent text-primary">
                                        <i className="bx bx-dollar-circle fs-5"></i>
                                    </span>
                                </div>
                                <div className="flex-fill">
                                    <p className="mb-1 text-muted fs-12">Total Platform Fees</p>
                                    <h5 className="fw-semibold mb-0">${summary?.total_platform_fees?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                    <div className="card custom-card overflow-hidden">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <span className="avatar avatar-md p-2 bg-success-transparent text-success">
                                        <i className="bx bx-trending-up fs-5"></i>
                                    </span>
                                </div>
                                <div className="flex-fill">
                                    <p className="mb-1 text-muted fs-12">Total Txn Value</p>
                                    <h5 className="fw-semibold mb-0">${summary?.total_transaction_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                    <div className="card custom-card overflow-hidden">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <span className="avatar avatar-md p-2 bg-info-transparent text-info">
                                        <i className="bx bx-check-double fs-5"></i>
                                    </span>
                                </div>
                                <div className="flex-fill">
                                    <p className="mb-1 text-muted fs-12">Total Finalized</p>
                                    <h5 className="fw-semibold mb-0">${summary?.total_finalized_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                    <div className="card custom-card overflow-hidden">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <span className="avatar avatar-md p-2 bg-warning-transparent text-warning">
                                        <i className="bx bx-transfer fs-5"></i>
                                    </span>
                                </div>
                                <div className="flex-fill">
                                    <p className="mb-1 text-muted fs-12">Txn Count</p>
                                    <h5 className="fw-semibold mb-0">{summary?.count || 0}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card custom-card">
                <div className="card-header justify-content-between">
                    <div className="card-title">
                        Transaction Breakdown
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-primary-transparent">
                            Showing {earnings.length} records
                        </span>
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table text-nowrap table-hover border-top">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">Gig Title</th>
                                    <th>Skillster</th>
                                    <th>Date</th>
                                    <th className="text-end">Txn Value</th>
                                    <th className="text-end">Platform Fee</th>
                                    <th className="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : earnings.length > 0 ? (
                                    earnings.map((item, index) => (
                                        <tr key={index}>
                                            <td className="ps-4" style={{ maxWidth: '250px' }}>
                                                <div className="d-flex align-items-center">
                                                    <div className="fw-semibold text-truncate">
                                                        {item.gig_title || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar avatar-sm avatar-rounded me-2 bg-light text-primary">
                                                        {item.skillster_name?.charAt(0) || 'U'}
                                                    </div>
                                                    <div>
                                                        <div className="fw-semibold fs-13">{item.skillster_name || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="fs-13 text-muted">
                                                    {item.transaction_date
                                                        ? new Date(item.transaction_date).toLocaleDateString('en-US', {
                                                            year: 'numeric', month: 'short', day: 'numeric'
                                                        })
                                                        : 'N/A'}
                                                </div>
                                                <div className="fs-11 text-muted">
                                                    {item.transaction_date
                                                        ? new Date(item.transaction_date).toLocaleTimeString('en-US', {
                                                            hour: '2-digit', minute: '2-digit'
                                                        })
                                                        : ''}
                                                </div>
                                            </td>
                                            <td className="text-end fw-semibold">
                                                ${item.transaction_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-end text-success fw-bold">
                                                ${item.platform_fee_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge bg-${item.status === 2 ? 'success' : 'warning'}-transparent`}>
                                                    {item.status === 2 ? 'Completed' : 'Pending'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            No earnings data found for the selected filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="card-footer border-top-0">
                    <div className="d-flex align-items-center">
                        <div>
                            Showing {earnings.length} Transactions
                        </div>
                        <div className="ms-auto">
                            {totalPages > 1 && (
                                <nav aria-label="Page navigation">
                                    <ul className="pagination mb-0">
                                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(page - 1)}>
                                                <i className="bx bx-chevron-left"></i>
                                            </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                                    {i + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                                                <i className="bx bx-chevron-right"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlatformEarnings;
